/* DM TELCO - THE PRODUCT SEARCH
   One search for the products page and the header (assets/dm-catalogue.js) and for the Old Skool
   Pricelist (assets/dm-pricelist.js). Owner, 2026-10-14: the two "should have the same search
   functionality", and "I really want it to be rock solid in a way of being able to search and find
   things really, really well". Needs assets/dm-minisearch.js loaded first.
   window.dmSearch.build(products, { synonyms, skip }) -> { search(query, { limit }) }.
   Measured against the real catalogue: tests/dm-search-quality.cjs. */
(function () {
  'use strict';
  if (window.dmSearch) return;
  // A list looked up by a word someone typed, or by the first part of a tag. Made with nothing
  // inherited, so the word "constructor" finds nothing instead of a piece of JavaScript.
  var dict = function (from) { var d = Object.create(null); if (from) Object.keys(from).forEach(function (k) { d[k] = from[k]; }); return d; };

  /* ═══ The search ═══════════════════════════════════════════════
     Two kinds of thing are typed into a parts search, and they need different handling:
       - a CODE (LEK104, ubc16120, "EK10"): matched on the item code with its punctuation and
         case ignored: whole, from the start, or anywhere inside it;
       - WORDS (cable tie, stainless bolt m10, "laddr"): matched by MiniSearch, a proven
         open-source engine (assets/dm-minisearch.js): whole words, the start of a word, and
         spelling mistakes.
     Every word typed must be found somewhere in the product, so more words narrow the list.
     Spelling help is only used for a word that finds nothing as typed, so a correct search is
     never padded out with look-alikes. If no product has every word, the ones with the most
     words are offered and the caller is told (`loose`).
     Tested against the real catalogue: tests/dm-search-quality.cjs (how well it finds things) and
     tests/dm-catalogue-smoke.cjs (the products page using it). */
  // Characters read the way the catalogue writes them (search review, 2026-10-14): "16mm²" is 16mm2,
  // "½" is 1/2, "café" is cafe. Any symbol that is not a letter or a digit splits words as before.
  var fold = function (s) {
    var t = String(s == null ? '' : s);
    try { t = t.normalize('NFKD'); } catch (e) { /* an old browser: as typed */ }
    return t.replace(/⁄/g, '/').replace(/[̀-ͯ]/g, '').toLowerCase();
  };
  var plain = function (s) { return fold(s).replace(/[^a-z0-9]+/g, ''); };
  var NOISE = dict({ x: 1, a: 1, an: 1, the: 1, and: 1, of: 1, to: 1, for: 1, with: 1, in: 1, on: 1, by: 1 });
  // Words as people type them: lower case, split on anything that is not a letter, a digit, a dot
  // or a slash, so "2.5mm" and "7/8" stay whole.
  var words = function (s) {
    return fold(s).replace(/["'`’]/g, '').split(/[^a-z0-9./]+/)
      .map(function (w) { return w.replace(/^[./]+|[./]+$/g, ''); }).filter(Boolean);
  };
  // What a product is filed under, beyond its words as written:
  //   "U-Bolt" is also found as "ubolt"; "M32", "25mm" and "NG32" are also found as "32" and "25";
  //   "Optus/TPG" is also found as "optus" and "tpg", "150/300" as "150" and "300" (a fraction such
  //   as 1/2 or 5/8 stays whole: "2" must not find every half-inch product).
  var indexWords = function (s) {
    var raw = fold(s);
    var out = words(raw);
    (raw.match(/[a-z0-9]+(?:[-/][a-z0-9]+)+/g) || []).forEach(function (j) { if (/[a-z]/.test(j)) out.push(j.replace(/[^a-z0-9]/g, '')); });
    out.slice().forEach(function (w) {
      if (/[a-z]/.test(w) && /\d/.test(w)) (w.match(/\d+(?:\.\d+)?/g) || []).forEach(function (d) { out.push(d); });
      // (a part that mixes letters and digits, the 16A of "NEMA 2/16A", is left as it was: "16a" must
      //  find 16 amp breakers, not 36 ladder parts; independent review 2026-10-14)
      if (w.indexOf('/') > 0) w.split('/').forEach(function (part) { if (/^[a-z]+$/.test(part) || /^\d{2,}(\.\d+)?$/.test(part)) out.push(part); });
    });
    return out;
  };
  // "lugs" finds "lug", "boxes" finds "box": the plural is taken off a word of four letters or more.
  var singular = function (w) {
    if (/\d/.test(w)) return w;
    if (w.length > 4 && /[^aeiou]ies$/.test(w)) return w.slice(0, -3) + 'y';
    if (w.length > 4 && /(ches|shes|xes|sses)$/.test(w)) return w.slice(0, -2);
    if (w.length > 3 && /s$/.test(w) && !/(ss|us|is)$/.test(w)) return w.slice(0, -1);
    return w;
  };
  var term = function (t) { return NOISE[t] ? null : singular(t); };
  // Units typed apart from their number ("6 mm", "10 a"): words, not stray letters of a code.
  var UNITS = dict({ mm: 1, cm: 1, m: 1, km: 1, a: 1, v: 1, w: 1, kw: 1, kv: 1, ma: 1, hz: 1, kg: 1, g: 1, pk: 1, ft: 1, in: 1, l: 1, ml: 1 });
  var isNumber = function (t) { return /^[\d./]+$/.test(t); };
  var hasDigit = function (t) { return /\d/.test(t); };

  // "galvanised, galv, gal = HDG" -> { galvanised: [['hdg']], galv: [['hdg']], gal: [['hdg']] }.
  // One rule a line: what people type, an equals sign, what the catalogue calls it. The search
  // then looks for BOTH, so a rule can only ever find more: "bolt = set screw" still finds the
  // hook bolts. Plurals need not be listed, and two rules for the same word add up.
  function readSynonyms(text) {
    var map = dict();
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('=');
      if (parts.length !== 2) return;
      var to = words(parts[1]);
      if (!to.length) return;
      parts[0].split(',').forEach(function (from) {
        var key = words(from).map(singular).join(' ');
        if (!key || key === to.map(singular).join(' ')) return;
        if (!map[key]) map[key] = [];
        map[key].push(to);
      });
    });
    return map;
  }

  function buildSearch(products, options) {
    var MiniSearch = window.MiniSearch && window.MiniSearch.default ? window.MiniSearch.default : window.MiniSearch;
    var synonyms = readSynonyms(options && options.synonyms);
    var skip = (options && options.skip) || {};
    var docs = products.map(function (p, n) {
      var facets = [];
      (p.g || []).forEach(function (tag) {
        var i = tag.indexOf('_');
        if (i <= 0) return;
        var val = tag.slice(i + 1).trim();
        if (!val || skip[tag.slice(0, i).trim().toLowerCase()]) return;
        facets.push(val);
      });
      return { id: n, title: p.t || '', code: p.s || '', type: p.y || '', brand: p.b || '', facets: facets.join(' '), text: p.d || '', extra: (p.x || []).join(' ') };
    });
    var mini = new MiniSearch({
      fields: ['title', 'code', 'type', 'brand', 'facets', 'text', 'extra'],
      storeFields: [],
      tokenize: indexWords,
      processTerm: term,
      searchOptions: { boost: { title: 4, code: 3, type: 2, brand: 2, facets: 1.5, extra: 1.5, text: 0.4 }, tokenize: words, processTerm: term },
    });
    mini.addAll(docs);
    var codes = products.map(function (p) { return plain(p.s); });
    var extras = products.map(function (p) { return (p.x || []).map(plain); });
    // A code with its dots kept: 2.5 is not 25 (legal brief 2026-10-05; independent review 2026-10-14).
    // And a code as its pieces: "cal2 5 6" is CAL2.5-6 (the space where its dot is), "nls 30" the start of NLS 30424.
    var dotted = function (s) { return fold(s).replace(/[^a-z0-9.]+/g, ''); };
    var pieces = function (s) { return fold(s).split(/[^a-z0-9]+/).filter(Boolean).join(' '); };
    var codesDot = products.map(function (p) { return dotted(p.s); });
    var codesPiece = products.map(function (p) { return pieces(p.s); });
    var extrasDot = products.map(function (p) { return (p.x || []).map(dotted); });
    var extrasPiece = products.map(function (p) { return (p.x || []).map(pieces); });
    var order = products.map(function (p) { return p.o == null ? 1e9 : Number(p.o); });
    var names = products.map(function (p) { return words(p.t).map(singular).join(' '); });

    // A word may be finished off ("stainles" -> stainless) unless it is a bare number: "25" must not find "250".
    var prefix = function (t) { return t.length >= 2 && !isNumber(t); };
    var noFuzz = function () { return 0; };

    function wordHits(raw) {
      var typed = words(raw).filter(function (w) { return !NOISE[w]; });
      if (!typed.length) return { hits: [], loose: false };
      // Each thing typed is a slot to fill: the word itself, or any other way the catalogue says it
      // (the theme setting), one word or two at a time ("zip tie").
      var slots = [];
      for (var i = 0; i < typed.length; i++) {
        var two = i + 1 < typed.length ? synonyms[singular(typed[i]) + ' ' + singular(typed[i + 1])] : null;
        if (two) { slots.push([[typed[i], typed[i + 1]]].concat(two)); i++; continue; }
        slots.push([[typed[i]]].concat(synonyms[singular(typed[i])] || []));
      }
      var ask = function (how, fuzzy) {
        return mini.search({
          combineWith: how,
          queries: slots.map(function (alts) {
            if (alts.length === 1 && alts[0].length === 1) return alts[0][0];
            return { combineWith: 'OR', queries: alts.map(function (a) { return a.length === 1 ? a[0] : { combineWith: 'AND', queries: a }; }) };
          }),
        }, { prefix: prefix, fuzzy: fuzzy });
      };
      var found = function (t) { return mini.search(t, { prefix: prefix, fuzzy: noFuzz }).length > 0; };
      // the word itself, not only as the start of a longer one
      var foundWhole = function (t) { return mini.search(t, { prefix: false, fuzzy: noFuzz }).length > 0; };
      // What a product's name would start with if it were named for this search: the words as
      // typed, and as the catalogue says them ("zip tie" -> "cable tie").
      var leads = function () {
        var as = slots.map(function (alts) { return alts[0].map(singular).join(' '); }).join(' ');
        var also = slots.map(function (alts) { return (alts[1] || alts[0]).map(singular).join(' '); }).join(' ');
        return as === also ? [as] : [as, also];
      };
      var hits = ask('AND', noFuzz);
      if (hits.length) return { hits: hits, loose: false, leads: leads() };
      // Nothing has every word as typed. Which words find nothing on their own? Those are the ones
      // that may be misspelt. In order: two letters the wrong way round ("cabel"), then one wrong
      // letter ("laddr", "tye"), then, for a long word only, two.
      var weak = dict();
      var corrected = false;
      slots.forEach(function (alts) {
        if (alts.length !== 1 || alts[0].length !== 1) return;
        var t = alts[0][0];
        var p = term(t);
        if (!p || hasDigit(p) || p.length < 3 || found(t)) return;
        for (var k = 0; k + 1 < t.length; k++) {
          var swapped = t.slice(0, k) + t[k + 1] + t[k] + t.slice(k + 2);
          if (swapped !== t && found(swapped)) { corrected = true; alts[0][0] = swapped; return; }
        }
        weak[p] = true;
      });
      var tries = Object.keys(weak).length ? [1, 2] : [0];
      var fuzzy = noFuzz;
      for (var n = 0; n < tries.length; n++) {
        fuzzy = (function (far) { return function (t) { return !weak[t] ? 0 : far === 1 || t.length < 6 ? 1 : 2; }; })(tries[n]);
        hits = ask('AND', fuzzy);
        if (hits.length) return { hits: hits, loose: false, corrected: corrected || tries[n] > 0, leads: leads() };
      }
      // "The closest" is offered only for a search with a real word in it, and no stray letters: a
      // word of three letters or more, as typed, that the catalogue has whole ("nut m14", "tie 1000",
      // "bolt purple", "cat6-patch"), and no piece of one or two letters that is not a unit or one of
      // the theme's other words ("AD TH GAL"). Pieces of a code ("AD-TH-GAL", "2 5RDBL20M", "GAP 38")
      // match whole or not at all: with competitor numbers switched off, one came back as Cold Gal
      // Spray (search review and two independent reviews, 2026-10-14).
      var real = slots.some(function (alts) { return alts[0].some(function (t) { return /^[a-z]{3,}$/.test(t) && foundWhole(t); }); });
      // (a two-letter word the catalogue itself uses, RF or DC or LC, is a word: "rf hazard sign 300"; third review)
      var stray = typed.some(function (t) { return /^[a-z]{1,2}$/.test(t) && !UNITS[t] && !synonyms[singular(t)] && !foundWhole(t); });
      if (slots.length < 2 || !real || stray) return { hits: [], loose: false };
      // No product has every word: offer the ones with the most of them. The first words typed
      // say what the thing is, the later ones narrow it ("cable tie 300"), so they count for more.
      hits = ask('OR', fuzzy);
      var slotOf = dict();
      slots.forEach(function (alts, k) {
        alts.forEach(function (a) { a.forEach(function (t) { var p = term(t); if (p && slotOf[p] == null) slotOf[p] = k; }); });
      });
      var most = 0;
      hits.forEach(function (h) {
        var filled = dict();
        h.queryTerms.forEach(function (t) { if (slotOf[t] != null) filled[slotOf[t]] = true; });
        h.weight = 0;
        Object.keys(filled).forEach(function (k) { h.weight += 100 + (slots.length - Number(k)); });
        most = Math.max(most, h.weight);
      });
      hits = hits.filter(function (h) { return h.weight === most; });
      return { hits: hits, loose: hits.length > 0, leads: leads() };
    }

    // -> [{ i (which product), score }] best first, with .total, .loose (not every word was found)
    //    and .corrected (a spelling mistake was allowed for).
    function search(query, opts) {
      var raw = String(query || '').trim();
      var none = [];
      none.total = 0; none.loose = false; none.corrected = false;
      if (!raw) return none;
      var typed = raw.toLowerCase().replace(/\s+/g, ' ');
      var q = plain(raw);
      var score = new Map();
      var bump = function (i, s) { if (s > (score.get(i) || 0)) score.set(i, s); };

      // 1. as a code. Typed as one piece: the whole code, its start, or inside it. Typed with spaces
      //    (a code that has a space in it, "NDB1-63 C6-1", or spaces for its dashes, "gce500 1 0 x"):
      //    the whole code, or its start broken where the code is broken ("nls 30" for NLS 30424)
      //    (search review, 2026-10-14). The whole code, best first: exactly as typed; broken in the
      //    same places ("cal2 5 6" is CAL2.5-6); the same with its dots kept ("cal 25 6" is CAL25-6, not
      //    CAL2.5-6); the same ignoring all punctuation. 2.5RDBL20M and 25RDBL20M are different
      //    products (legal brief 2026-10-05).
      var piece = !/\s/.test(raw);
      var qd = dotted(raw);
      var qp = pieces(raw);
      if (q.length >= 2) {
        for (var i = 0; i < codes.length; i++) {
          var c = codes[i];
          if (c) {
            if (c === q) {
              bump(i, String(products[i].s).toLowerCase().replace(/\s+/g, ' ') === typed ? 1000
                : codesPiece[i] === qp ? 975 : codesDot[i] === qd ? 960 : 950);
            }
            else if (piece && q.length >= 3 && c.indexOf(q) === 0) bump(i, 800 - (c.length - q.length));
            else if (piece && q.length >= 4 && c.indexOf(q) > 0) bump(i, 600 - (c.length - q.length));
            // the start of a code that has a space in it, broken where it is ("nls 30", "ndb1-63 c"):
            // found, but after anything the words find ("ndb1 6" is the 6 A breaker first; second review)
            else if (!piece && q.length >= 4 && codesPiece[i].indexOf(qp) === 0) {
              bump(i, 450 - (c.length - q.length));
            }
          }
          var xs = extras[i];
          for (var k = 0; k < xs.length; k++) {
            if (xs[k] === q) bump(i, extrasPiece[i][k] === qp || extrasDot[i][k] === qd ? 925 : 900);
            else if (piece && q.length >= 4 && xs[k].indexOf(q) === 0) bump(i, 700);
          }
        }
      }
      var byCode = score.size;

      // 2. as words. A code that matched is the answer: look-alike words are not added to it.
      var w = wordHits(raw);
      var loose = false;
      var corrected = false;
      if (!(byCode && (w.loose || w.corrected))) {
        var top = w.hits.length ? w.hits[0].score : 1;
        w.hits.forEach(function (h) {
          var s = 100 + 400 * (h.score / top);
          // a product NAMED for what was typed comes before one that only mentions it
          if (w.leads && w.leads.some(function (l) { return names[h.id].indexOf(l) === 0; })) s += 150;
          bump(h.id, s);
        });
        loose = !byCode && w.loose;
        corrected = !byCode && !!w.corrected;
      }

      var list = Array.from(score.entries()).map(function (e) { return { i: e[0], score: e[1] }; });
      list.sort(function (a, b) { return b.score - a.score || order[a.i] - order[b.i] || a.i - b.i; });
      var res = opts && opts.limit ? list.slice(0, opts.limit) : list;
      res.total = list.length; res.loose = loose; res.corrected = corrected;
      return res;
    }
    return { search: search, size: products.length };
  }

  /* The public catalogue: every product as any product page shows it (no price, nothing of a customer),
     in the shape and under the key the products page keeps it in this browser for an hour
     (assets/dm-catalogue.js loadIndex). The price list uses it to search the same words the products
     page does: the tags (size, material, colour...) and the start of the description. Read when it is
     kept and under an hour old; otherwise asked for, 250 products a request, and kept the same way.
     Resolves to [] when it cannot be had: the caller then searches what it has of its own. */
  var KEEP_MS = 60 * 60 * 1000;
  function publicCatalogue(opts) {
    var key = 'dmCat:index:1:' + (opts && opts.trade ? 'trade' : 'guest');
    var url = (opts && opts.url) || '/collections/all';
    var cached = null;
    try { cached = JSON.parse(localStorage.getItem(key)); } catch (e) { /* storage blocked or empty */ }
    var age = cached && cached.t ? Date.now() - cached.t : Infinity;
    if (cached && Array.isArray(cached.products) && cached.products.length > 0 && age >= 0 && age < KEEP_MS) {
      return Promise.resolve(cached.products.filter(Boolean));
    }
    var page = function (n) {
      return fetch(url + (url.indexOf('?') < 0 ? '?' : '&') + 'section_id=dm-catalogue-index&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status)); })
        .then(function (html) {
          var el = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-dm-catalogue-index]');
          if (!el) throw new Error('no catalogue in the answer');
          return JSON.parse(el.textContent);
        });
    };
    return page(1).then(function (first) {
      var rest = [];
      for (var n = 2; n <= Math.min(Number(first.pages) || 1, 40); n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var products = [];
        [first].concat(more).forEach(function (m) { products = products.concat(m.products || []); });
        // null = a trade-only product, left out for a visitor (snippets/dm-catalogue-item.liquid)
        products = products.filter(Boolean);
        try {
          localStorage.setItem(key, JSON.stringify({ t: Date.now(), products: products, collections: first.collections || [] }));
          localStorage.setItem(key + ':t', String(Date.now()));
        } catch (e) { /* storage full or blocked */ }
        return products;
      });
    }).catch(function (err) {
      if (window.console) console.warn('DM search: the public catalogue could not be had', err);
      return [];
    });
  }

  window.dmSearch = { build: buildSearch, words: words, indexWords: indexWords, singular: singular, term: term, isNumber: isNumber, plain: plain, publicCatalogue: publicCatalogue };
})();
